import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAll';
import { fetchAllIn, fetchAllInResult } from '../lib/adminCounts';
import { parseCustomLinkEntityId } from '../../lib/customLinks';
import { useTabParam } from '../../hooks/useTabParam';
import { logAudit } from '../lib/auditLog';
// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(ts) {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Avatar ────────────────────────────────────────────────────────────────────
function Avatar({ username, avatarUrl, size = 36 }) {
  const initials = (username || 'U').slice(0, 2).toUpperCase();
  const palettes = [
    { bg: '#fef3c7', color: 'var(--feedback-warning-ink)' },
    { bg: '#d1fae5', color: '#065f46' },
    { bg: '#dbeafe', color: '#1e40af' },
    { bg: '#f3e8ff', color: '#6b21a8' },
    { bg: '#fee2e2', color: '#991b1b' },
    { bg: '#e0f2fe', color: '#0369a1' },
  ];
  const { bg, color } = palettes[(username || 'a').charCodeAt(0) % palettes.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: 'var(--radius-circle)', flexShrink: 0,
      background: avatarUrl ? `url(${avatarUrl}) center/cover` : bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.32), fontWeight: 700, color,
      overflow: 'hidden',
    }}>
      {!avatarUrl && initials}
    </div>
  );
}

// ── Reason pill ───────────────────────────────────────────────────────────────
function ReasonPill({ reason }) {
  const map = {
    'unmarked spoiler': { bg: '#fef3c7', color: 'var(--feedback-warning-ink)',  border: 'var(--feedback-warning-border)' },
    'spam':             { bg: '#fef3c7', color: 'var(--feedback-warning-ink)',  border: 'var(--feedback-warning-border)' },
    'harassment':       { bg: '#fee2e2', color: '#991b1b',  border: '#fca5a5' },
    'hate speech':      { bg: '#fee2e2', color: '#991b1b',  border: '#fca5a5' },
    'misleading tags':  { bg: '#dbeafe', color: '#1e40af',  border: '#bfdbfe' },
    'wrong metadata':   { bg: '#dbeafe', color: '#1e40af',  border: '#bfdbfe' },
    'duplicate':        { bg: '#f5f0e8', color: 'var(--neutral-600)',  border: 'var(--brand-sand)' },
    'impersonation':    { bg: '#f3e8ff', color: '#6b21a8',  border: '#d8b4fe' },
  };
  const s = map[(reason || '').toLowerCase()] || { bg: '#f5f0e8', color: 'var(--neutral-600)', border: 'var(--brand-sand)' };
  return (
    <span style={{
      fontSize: 'var(--font-size-xs)', fontWeight: 700, padding: '3px 10px', borderRadius: 'var(--radius-10)',
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {reason}
    </span>
  );
}

// What a pipeline's status means for the person reading its report.
const PIPELINE_STATUS_NOTE = {
  approved: 'Approved: can show on the homepage.',
  pending:  'Pending: not on the homepage yet.',
  hidden:   'Already hidden: not on the homepage.',
  rejected: 'Rejected: not on the homepage.',
};

// ── Single report card — flag-card style matching screenshot ──────────────────
function ReportCard({ report, onAction, working }) {
  const busy = working === report.id;
  const urgent = report.reporter_count >= 2;

  return (
    <div style={{
      background: urgent ? '#fff9f9' : '#fffdf9',
      border: '1px solid var(--brand-sand)',
      borderLeft: `3px solid ${urgent ? '#dc2626' : '#f59e0b'}`,
      borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 'var(--space-md)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-md)', marginBottom: report.content_preview ? 10 : 0 }}>
        <Avatar username={report.username} avatarUrl={report.avatar_url} size={36} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 3, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--neutral-800)' }}>
              {report.entity_type === 'book' || report.entity_type === 'pipeline'
                ? report.username  // book title+author, or popular → hidden gem
                : `@${report.username}`}
            </span>
            <ReasonPill reason={report.reason} />
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--neutral-500)' }}>
            {timeAgo(report.created_at)} · reported by <strong style={{ color: report.reporter_count >= 2 ? 'var(--feedback-error)' : 'var(--neutral-800)' }}>{report.reporter_count} user{report.reporter_count !== 1 ? 's' : ''}</strong>
          </div>
        </div>
      </div>

      {/* Content preview (posts only) */}
      {report.content_preview && (
        <div style={{
          margin: '10px 0 12px', padding: '10px 14px',
          background: 'rgba(0,0,0,0.03)', border: '1px solid var(--brand-cream)',
          borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', color: 'var(--neutral-600)',
          fontFamily: 'var(--font-display)', fontStyle: 'italic', lineHeight: 1.6,
        }}>
          "{report.content_preview}"
        </div>
      )}

      {/* The pipeline as it stands now: both books, and whether readers can still see it */}
      {report.pipeline && (
        <div style={{
          margin: '10px 0 12px', padding: '10px 14px',
          background: 'rgba(0,0,0,0.03)', border: '1px solid var(--brand-cream)',
          borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', lineHeight: 1.7,
        }}>
          {report.pipeline.found ? <>
            <div><strong>Popular book:</strong> {report.pipeline.popular}</div>
            <div><strong>Hidden gem:</strong> {report.pipeline.gem}</div>
            <div style={{ marginTop: 4, fontWeight: 600, color: report.pipeline.status === 'approved' ? 'var(--feedback-error)' : 'var(--neutral-500)' }}>
              {PIPELINE_STATUS_NOTE[report.pipeline.status] || `Status: ${report.pipeline.status}`}
            </div>
          </> : (
            <div>{report.pipeline.note}</div>
          )}
        </div>
      )}

      {/* Reporter note */}
      {report.notes && (
        <div style={{
          margin: '8px 0 12px', padding: '8px 12px',
          background: '#fffbeb', border: '1px solid var(--feedback-warning-border)',
          borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', color: 'var(--feedback-warning-ink)',
        }}>
          <strong>Note:</strong> {report.notes}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 'var(--space-10)' }}>
        <Btn variant="approve" onClick={() => onAction('dismiss', report)} disabled={busy}>Dismiss</Btn>

        {report.entity_type === 'post' && <>
          <Btn onClick={() => onAction('warnUser', report)} disabled={busy}>Warn user</Btn>
          <Btn variant="reject" onClick={() => onAction('removePost', report)} disabled={busy}>Remove post</Btn>
        </>}

        {report.entity_type === 'book' && <>
          <Btn onClick={() => onAction('editTags', report)} disabled={busy}>Edit tags</Btn>
          <Btn variant="reject" onClick={() => onAction('removeBook', report)} disabled={busy}>Remove</Btn>
        </>}

        {report.entity_type === 'custom_link' && <>
          <Btn onClick={() => onAction('warnUser', report)} disabled={busy}>Warn user</Btn>
          <Btn variant="reject" onClick={() => onAction('removeLink', report)} disabled={busy}>Remove link</Btn>
        </>}

        {report.entity_type === 'user' && <>
          <Btn onClick={() => onAction('warnUser', report)} disabled={busy}>Warn user</Btn>
          <Btn variant="reject" onClick={() => onAction('suspendUser', report)} disabled={busy}>Suspend</Btn>
        </>}

        {/* No Delete: pipelines has no delete policy, and an author's next save would
            make a deleted comp pipeline again. Hide is the removal that sticks. Warn user
            only when the report names someone: the homepage sends the pipeline's
            created_by, which can be empty. */}
        {report.entity_type === 'pipeline' && <>
          {report.reported_user_id && (
            <Btn onClick={() => onAction('warnUser', report)} disabled={busy}>Warn user</Btn>
          )}
          <Btn variant="reject" onClick={() => onAction('hidePipeline', report)}
               disabled={busy || !report.pipeline?.canHide}
               title={report.pipeline?.canHide ? 'Takes it off the homepage' : 'Nothing to hide: Dismiss closes the report'}>
            Hide
          </Btn>
        </>}
      </div>
    </div>
  );
}

function Btn({ children, onClick, variant = 'default', disabled, title }) {
  const v = {
    default: { bg: '#fff',    color: 'var(--neutral-600)', border: 'var(--brand-sand)' },
    approve: { bg: '#f0fdf4', color: '#065f46', border: '#86efac' },
    reject:  { bg: '#fff8f8', color: 'var(--feedback-error)', border: '#fca5a5' },
  }[variant];
  return (
    <button onClick={onClick} disabled={disabled} title={title} style={{
      fontSize: 12.5, fontWeight: 600, padding: '7px 14px', borderRadius: 'var(--radius-md)',
      cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
      opacity: disabled ? 0.5 : 1, background: v.bg, color: v.color,
      border: `1.5px solid ${v.border}`, fontFamily: 'var(--font-body)',
    }}>
      {children}
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ══════════════════════════════════════════════════════════════════════════════
export default function ReportsSection({ onBadgeChange }) {
  const [reports, setReports]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [working, setWorking]   = useState(null);
  const [tab, setTab]           = useTabParam('tab', 'post')
  const onBadgeRef = useRef(onBadgeChange);
  useEffect(() => { onBadgeRef.current = onBadgeChange; }, [onBadgeChange]);

  const load = useCallback(async () => {
    setLoading(true);

    // Every open report. There is no .limit() here, but Supabase caps a
    // response at 1,000 rows anyway, and the four tab counts and the header
    // total are all lengths of what comes back — so past that they would
    // silently stop moving and the extra reports would never appear.
    const rawReports = await fetchAllRows(() => supabase
      .from('reports')
      .select('id, entity_type, entity_id, reported_user_id, reason, notes, status, created_at')
      .eq('status', 'open'));

    // Group multiple reports on the same entity into one card. The grouping
    // keeps the first row it sees per entity, so newest-first has to be
    // reapplied here — fetchAllRows pages by id, not by date.
    const ordered = [...rawReports].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    const grouped = {};
    ordered.forEach(r => {
      const key = `${r.entity_type}:${r.entity_id}`;
      if (!grouped[key]) {
        grouped[key] = {
          id: r.id, entity_type: r.entity_type,
          entity_id: r.entity_id, reported_user_id: r.reported_user_id,
          reason: r.reason, notes: r.notes,
          created_at: r.created_at,
          reporter_count: 0, report_ids: [],
          username: '', avatar_url: null, content_preview: null,
        };
      }
      grouped[key].reporter_count++;
      grouped[key].report_ids.push(r.id);
    });

    const entries = Object.values(grouped);

    // ── Enrich posts ───────────────────────────────────────────────────────
    const postEntries = entries.filter(e => e.entity_type === 'post');
    if (postEntries.length) {
      const postRows = await fetchAllIn('posts', 'id, content, user_id', 'id', postEntries.map(e => e.entity_id));
      const postMap = {};
      (postRows || []).forEach(p => { postMap[String(p.id)] = p; });
      const userIds = [...new Set((postRows || []).map(p => p.user_id).filter(Boolean))];
      let profileMap = {};
      if (userIds.length) {
        const profiles = await fetchAllIn('user_profiles', 'id, user_id, username, avatar_url', 'user_id', userIds);
        (profiles || []).forEach(p => { profileMap[p.user_id] = p; });
      }
      postEntries.forEach(e => {
        const post = postMap[e.entity_id];
        const prof = post ? profileMap[post.user_id] : null;
        e.username          = prof?.username   || 'reader';
        e.avatar_url        = prof?.avatar_url || null;
        e.reported_user_id  = post?.user_id || e.reported_user_id;
        e.content_preview   = post?.content
          ? (post.content.length > 160 ? post.content.slice(0, 160) + '…' : post.content)
          : null;
      });
    }

    // ── Enrich books ───────────────────────────────────────────────────────
    const bookEntries = entries.filter(e => e.entity_type === 'book');
    if (bookEntries.length) {
      const bookRows = await fetchAllIn('books', 'id, title, author_name', 'id', bookEntries.map(e => e.entity_id));
      const bookMap = {};
      (bookRows || []).forEach(b => { bookMap[String(b.id)] = b; });
      bookEntries.forEach(e => {
        const book = bookMap[e.entity_id];
        e.username = book ? `${book.title}, ${book.author_name}` : 'Unknown book';
      });
    }

    // ── Enrich user reports ────────────────────────────────────────────────
    const userEntries = entries.filter(e => e.entity_type === 'user');
    if (userEntries.length) {
      const userIds = [...new Set(userEntries.map(e => e.reported_user_id).filter(Boolean))];
      if (userIds.length) {
        const profiles = await fetchAllIn('user_profiles', 'id, user_id, username, avatar_url', 'user_id', userIds);
        const profileMap = {};
        (profiles || []).forEach(p => { profileMap[p.user_id] = p; });
        userEntries.forEach(e => {
          const prof = profileMap[e.reported_user_id];
          e.username   = prof?.username   || 'reader';
          e.avatar_url = prof?.avatar_url || null;
        });
      }
    }

    // ── Enrich event reports ──────────────────────────────────────────────
    const eventEntries = entries.filter(e => e.entity_type === 'event');
    if (eventEntries.length) {
      // `title` -- profile_events has no such column. It is `name`
      // (live_schema_snapshot.sql:2479). PostgREST answered 42703 on every
      // page, fetchAllRows logged it and returned [], and so every event
      // report in this queue rendered as a bare "Event #123" with no name, no
      // avatar and nothing saying why.
      //
      // fetchAllInResult rather than fetchAllIn so a future failure cannot
      // disguise itself as "those events were deleted" the way this one did.
      const { ok: eventsOk, rows: eventRows, error: eventsErr } =
        await fetchAllInResult('profile_events', 'id, name, user_id', 'id', eventEntries.map(e => e.entity_id));
      if (!eventsOk) console.error('ReportsSection: could not load reported events:', eventsErr);
      const eventMap = {};
      (eventRows || []).forEach(ev => { eventMap[String(ev.id)] = ev; });
      const userIds = [...new Set((eventRows || []).map(ev => ev.user_id).filter(Boolean))];
      let profileMap = {};
      if (userIds.length) {
        const profiles = await fetchAllIn('user_profiles', 'id, user_id, username, avatar_url', 'user_id', userIds);
        (profiles || []).forEach(p => { profileMap[p.user_id] = p; });
      }
      eventEntries.forEach(e => {
        const ev   = eventMap[e.entity_id];
        const prof = ev ? profileMap[ev.user_id] : null;
        // Three different situations, and they used to print the same string.
        // An admin deciding what to do about a report needs to know which:
        // a named event is actionable, a deleted one needs no action, and a
        // failed lookup means try again rather than assume anything.
        e.username = ev?.name
          ? `Event: ${ev.name}`
          : eventsOk
            ? `Event #${e.entity_id} (deleted)`
            : `Event #${e.entity_id} (could not load)`;
        e.avatar_url       = prof?.avatar_url || null;
        e.content_preview  = ev?.name || null;
      });
    }

    // ── Enrich custom-link reports ────────────────────────────────────────
    // entity_id is <profile_user_id>:<block_id>:<link_id>, not a row id, so
    // the profile is looked up from the first segment rather than from
    // reported_user_id -- that column is nullable on older rows and this
    // screen has to work for them too.
    const linkEntries = entries.filter(e => e.entity_type === 'custom_link');
    if (linkEntries.length) {
      const refs = linkEntries.map(e => parseCustomLinkEntityId(e.entity_id));
      const ownerIds = [...new Set(refs.filter(Boolean).map(r => r.profileUserId))];
      let ownerMap = {};
      if (ownerIds.length) {
        const owners = await fetchAllIn('user_profiles', 'user_id, username, avatar_url, profile_blocks', 'user_id', ownerIds);
        (owners || []).forEach(p => { ownerMap[p.user_id] = p; });
      }
      linkEntries.forEach((e, i) => {
        const ref   = refs[i];
        const owner = ref ? ownerMap[ref.profileUserId] : null;
        e.username   = owner?.username || 'unknown reader';
        e.avatar_url = owner?.avatar_url || null;

        // Show the link as it stands NOW, not the URL captured in notes at
        // report time. If the owner edited it since, the moderator is deciding
        // about the live one, and the reported URL is still in the note below.
        const block = (owner?.profile_blocks || []).find(b => b?.id === ref?.blockId);
        const link  = (block?.data?.links || []).find(l => l?.id === ref?.linkId);
        e.content_preview = link
          ? `${link.label || 'untitled'} → ${link.url}`
          : 'This link is no longer on the profile.';
      });
    }

    // ── Enrich pipeline reports ───────────────────────────────────────────
    // The titles come from the books the pipeline points at, by id, because the
    // title and author copies on the pipeline row are only a fallback (spec 0f).
    // The person to warn is the report's reported_user_id (the homepage sends the
    // pipeline's created_by), and they are looked up for their @name.
    //
    // fetchAllInResult, so a failed read says "could not load" rather than
    // passing for a pipeline that was deleted -- the same reason events use it.
    const pipelineEntries = entries.filter(e => e.entity_type === 'pipeline');
    if (pipelineEntries.length) {
      const { ok: plOk, rows: plRows, error: plErr } = await fetchAllInResult(
        'pipelines',
        'id, status, pop_book_id, gem_book_id, pop_book_title, pop_book_author, gem_book_title, gem_book_author',
        'id', pipelineEntries.map(e => e.entity_id));
      if (!plOk) console.error('ReportsSection: could not load reported pipelines:', plErr);
      const plMap = {};
      (plRows || []).forEach(pl => { plMap[String(pl.id)] = pl; });

      const bookIds = [...new Set((plRows || []).flatMap(pl => [pl.pop_book_id, pl.gem_book_id]).filter(Boolean))];
      const bookMap = {};
      if (bookIds.length) {
        const bookRows = await fetchAllIn('books', 'id, title, author_name', 'id', bookIds);
        (bookRows || []).forEach(b => { bookMap[String(b.id)] = b; });
      }

      const ownerIds = [...new Set(pipelineEntries.map(e => e.reported_user_id).filter(Boolean))];
      const ownerMap = {};
      if (ownerIds.length) {
        const owners = await fetchAllIn('user_profiles', 'id, user_id, username, avatar_url', 'user_id', ownerIds);
        (owners || []).forEach(p => { ownerMap[p.user_id] = p; });
      }

      const named = (book, title, author) => {
        const t = book?.title || title || 'untitled';
        const a = book?.author_name || author;
        return { title: t, text: a ? `${t}, ${a}` : t };
      };

      pipelineEntries.forEach(e => {
        const pl = plMap[e.entity_id];
        if (!pl) {
          e.username = plOk ? `Pipeline #${e.entity_id} (deleted)` : `Pipeline #${e.entity_id} (could not load)`;
          e.pipeline = {
            found: false, canHide: false,
            note: plOk
              ? 'This pipeline no longer exists. Dismiss closes the report.'
              : 'The pipeline could not be loaded, so it cannot be hidden from here. Reload to try again.',
          };
        } else {
          const popular = named(bookMap[String(pl.pop_book_id)], pl.pop_book_title, pl.pop_book_author);
          const gem     = named(bookMap[String(pl.gem_book_id)], pl.gem_book_title, pl.gem_book_author);
          e.username = `${popular.title} → ${gem.title}`;
          e.pipeline = {
            found: true, status: pl.status, popular: popular.text, gem: gem.text,
            // Hiding something already off the homepage changes nothing and would
            // still mark the report actioned, so it is not offered.
            canHide: pl.status === 'approved' || pl.status === 'pending',
          };
        }
        const owner = e.reported_user_id ? ownerMap[e.reported_user_id] : null;
        e.owner_username = owner?.username || (e.reported_user_id ? 'reader' : null);
      });
    }

    const allEnriched = entries.sort((a, b) =>
      new Date(b.created_at) - new Date(a.created_at)
    );

    setReports(allEnriched);
    onBadgeRef.current?.('reports', allEnriched.length);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleAction(action, report) {
    setWorking(report.id);
    try {
      // None of these checked whether the write landed, so a refused delete or
      // a row blocked by RLS still marked the report "actioned" and wrote an
      // audit entry saying it was handled. The report then disappears from the
      // queue with the reported content still live — the one outcome moderation
      // must never produce.
      const markStatus = async (status) => {
        const { error } = await supabase.from('reports')
          .update({ status })
          .in('id', report.report_ids);
        if (error) throw new Error(`report status not updated: ${error.message}`);
      };

      if (action === 'dismiss') {
        await markStatus('dismissed');
        await logAudit('update', report.entity_type, `Dismissed report on ${report.entity_type} ${report.entity_id}`, report.entity_id);
      }

      if (action === 'removePost') {
        // Delete first, mark second. If the delete fails this throws before the
        // report is marked handled, so it stays in the queue for another go.
        const { error } = await supabase.from('posts').delete().eq('id', report.entity_id);
        if (error) throw new Error(`Post NOT removed, and the report is still open: ${error.message}`);
        await markStatus('actioned');
        await logAudit('delete', 'post', `Removed reported post ${report.entity_id} by @${report.username}`, report.entity_id);
      }

      if (action === 'removeBook') {
        const { error } = await supabase.from('books').update({ status: 'removed' }).eq('id', report.entity_id);
        if (error) throw new Error(`Book NOT removed, and the report is still open: ${error.message}`);
        await markStatus('actioned');
        await logAudit('delete', 'book', `Removed reported book ${report.entity_id}`, report.entity_id);
      }

      if (action === 'removeLink') {
        // A custom link is not a row — it lives inside the jsonb array on
        // somebody else's user_profiles, which RLS refuses to let an admin
        // rewrite. admin_remove_custom_link (20260917000000_custom_links.sql)
        // is SECURITY DEFINER and does the admin check itself.
        const ref = parseCustomLinkEntityId(report.entity_id);
        if (!ref) throw new Error(`Link NOT removed: unreadable report reference "${report.entity_id}"`);

        const { data, error } = await supabase.rpc('admin_remove_custom_link', {
          p_user_id:  ref.profileUserId,
          p_block_id: ref.blockId,
          p_link_id:  ref.linkId,
        });
        if (error) throw new Error(`Link NOT removed, and the report is still open: ${error.message}`);

        // The function returns a discriminated result rather than throwing on
        // "already gone", so a false return here is a real answer, not a
        // failure. link_not_found means the owner deleted it first — that is
        // the outcome the report asked for, so the report closes. Anything
        // else means nothing was removed and the report stays open.
        if (data?.ok !== true) {
          if (data?.reason !== 'link_not_found') {
            throw new Error(`Link NOT removed (${data?.reason || 'unknown reason'}), and the report is still open.`);
          }
          await markStatus('actioned');
          await logAudit('delete', 'custom_link', `Reported link on @${report.username} was already gone`, report.entity_id);
        } else {
          await markStatus('actioned');
          await logAudit('delete', 'custom_link', `Removed reported link "${data.label || ''}" from @${report.username}`, report.entity_id);
        }
      }

      if (action === 'hidePipeline') {
        // Hide first, mark second, and only once the update hands the row back:
        // a refused update returns no error and changes no row, and marking the
        // report actioned on that would take it out of the queue with the pipeline
        // still on the homepage. A hidden pipeline leaves match_pipelines (approved
        // only), and an author's next save leaves a hidden comp pipeline alone.
        const { data: hidden, error } = await supabase.from('pipelines')
          .update({ status: 'hidden' })
          .eq('id', report.entity_id)
          .select('id');
        if (error) throw new Error(`Pipeline NOT hidden, and the report is still open: ${error.message}`);
        if (!hidden?.length) throw new Error('Pipeline NOT hidden: the update changed no row, and the report is still open.');
        await markStatus('actioned');
        await logAudit('update', 'pipeline', `Hid reported pipeline ${report.entity_id} (${report.username})`, report.entity_id);
      }

      if (action === 'warnUser') {
        // Mark dismissed + log — notification system to be built later
        await markStatus('actioned');
        // report.username is the owner for posts, links and users; a pipeline's
        // username is its title, so its owner is carried in owner_username.
        await logAudit('update', 'user', `Warned @${report.owner_username ?? report.username} regarding reported ${report.entity_type}`, report.reported_user_id);
      }

      if (action === 'suspendUser') {
        const reason = prompt(`Reason for suspending @${report.username}:`);
        if (reason === null) { setWorking(null); return; }
        const { error } = await supabase.from('user_profiles').update({
          suspended: true, suspended_at: new Date().toISOString(), suspend_reason: reason || null,
        }).eq('user_id', report.reported_user_id);
        if (error) throw new Error(`User NOT suspended, and the report is still open: ${error.message}`);
        await markStatus('actioned');
        await logAudit('update', 'user', `Suspended @${report.username} after report, "${reason}"`, report.reported_user_id);
      }

      load();
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setWorking(null);
  }

  const counts = {
    post:        reports.filter(r => r.entity_type === 'post').length,
    book:        reports.filter(r => r.entity_type === 'book').length,
    custom_link: reports.filter(r => r.entity_type === 'custom_link').length,
    user:        reports.filter(r => r.entity_type === 'user').length,
    event:       reports.filter(r => r.entity_type === 'event').length,
    pipeline:    reports.filter(r => r.entity_type === 'pipeline').length,
  };

  const TABS = [
    { id: 'post',  label: `Posts${counts.post   > 0 ? ` (${counts.post})`  : ''}` },
    { id: 'book',  label: `Books${counts.book   > 0 ? ` (${counts.book})`  : ''}` },
    { id: 'custom_link', label: `Links${counts.custom_link > 0 ? ` (${counts.custom_link})` : ''}` },
    { id: 'user',  label: `Users${counts.user   > 0 ? ` (${counts.user})`  : ''}` },
    { id: 'event', label: `Events${counts.event > 0 ? ` (${counts.event})` : ''}` },
    { id: 'pipeline', label: `Pipelines${counts.pipeline > 0 ? ` (${counts.pipeline})` : ''}` },
  ];

  const visible = reports.filter(r => r.entity_type === tab);
  const total   = reports.length;

  return (
    <div style={{ padding: '30px 36px', fontFamily: 'var(--font-body)' }}>

      {/* Header */}
      <div style={{ marginBottom: 26 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, lineHeight: 1.1, margin: 'var(--space-none)' }}>
          Reports
        </h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 'var(--space-xs)' }}>
          {total > 0
            ? `${total} open report${total !== 1 ? 's' : ''} · ${counts.post} post${counts.post !== 1 ? 's' : ''} · ${counts.book} book${counts.book !== 1 ? 's' : ''} · ${counts.user} user profile${counts.user !== 1 ? 's' : ''} · ${counts.event} event${counts.event !== 1 ? 's' : ''} · ${counts.custom_link} link${counts.custom_link !== 1 ? 's' : ''} · ${counts.pipeline} pipeline${counts.pipeline !== 1 ? 's' : ''}`
            : 'No open reports, all clear'}
        </p>
      </div>

      <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: '20px 22px' }}>

        {/* Placeholder note — matches screenshot */}
        <div style={{
          padding: '12px 16px', background: '#fffbeb', border: '1px solid var(--feedback-warning-border)',
          borderRadius: 'var(--radius-md)', fontSize: 12.5, color: 'var(--neutral-500)',
          marginBottom: 18, lineHeight: 1.6,
        }}>
          <strong style={{ color: 'var(--feedback-warning-ink)' }}>Placeholder categories below.</strong>{' '}
          Report reasons shown (spoiler, spam, harassment, misleading tags, duplicate) are suggestions based on common patterns.
          Lock the final list before launch so the reporting UI on book popups and profiles uses matching labels.
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '2px solid var(--brand-sand)', marginBottom: 18, marginTop: -4 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              fontSize: 'var(--font-size-sm)', fontWeight: 600, padding: '8px 16px',
              color: tab === t.id ? 'var(--neutral-800)' : 'var(--neutral-400)',
              border: 'none', background: 'transparent',
              borderBottom: tab === t.id ? '2px solid var(--neutral-800)' : '2px solid transparent',
              marginBottom: -2, cursor: 'pointer', fontFamily: 'var(--font-body)',
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 'var(--space-xxl)', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading reports...</div>
        ) : !visible.length ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
            ✓ No open {tab} reports
          </div>
        ) : (
          visible.map(report => (
            <ReportCard
              key={report.id}
              report={report}
              onAction={handleAction}
              working={working}
            />
          ))
        )}
      </div>
    </div>
  );
}